import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Switch, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllEmployees,
  getAllTasks,
  toggleEmployee,
} from "../../../redux/actions/authActions";
import SearchBar from "../../../components/searchbar/SearchBar";

const TeamManagementHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const employees = useSelector((state) => state.auth.allEmployees);
  const tasks = useSelector((state) => state.auth.allTasks);
  const [rows, setRows] = useState([]);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  useEffect(() => {
    if (token) {
      dispatch(getAllEmployees(token));
      dispatch(getAllTasks(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (employees && tasks) {
      const formatted = employees.map((emp) => {
        const empTasks = tasks.filter((task) =>
          task.assignees?.some(a => (a._id || a) === emp._id)
        );
        const completedTasks = empTasks.filter(
          (t) => t.status === "Completed"
        ).length;

        const uniqueClients = Array.from(
          new Set(empTasks.map((task) => task.client).filter(Boolean))
        );

        const progress =
          empTasks.length > 0
            ? Math.round((completedTasks / empTasks.length) * 100)
            : 0;

        return {
          ...emp,
          id: emp._id, // Keep _id for row key
          readableId: emp.id || emp._id, // Readable ID for display
          tasksAssigned: empTasks.length,
          progress,
          clientsAssigned: uniqueClients,
        };
      });
      setRows(formatted);
    }
  }, [employees, tasks]);

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await dispatch(toggleEmployee(id, newStatus));
      setRows((prev) =>
        prev.map((row) =>
          row._id === id ? { ...row, accountStatus: newStatus } : row
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (query) => {
    const searchText = query.toLowerCase();
    const filtered = employees.filter((emp) =>
      emp.name.toLowerCase().includes(searchText)
    );
    const filteredFormatted = filtered.map((emp) => ({
      ...emp,
      id: emp._id,
    }));
    setRows(filteredFormatted);
  };

  const handleOpenMenu = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRowId(id);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedRowId(null);
  };

  const handleViewProfile = (id) => {
    navigate(`profile/${id}`);
  };

  const handleViewTasks = (id) => {
    navigate(`member-task?memberId=${id}`);
  };

  const columns = [
    { field: "readableId", headerName: "Member ID", width: 120 },
    { field: "name", headerName: "Member Name", width: 180 },
    {
      field: "createdAt",
      headerName: "Created On",
      width: 160,
      renderCell: (params) => new Date(params.value).toDateString(),
    },
    {
      field: "clientsAssigned",
      headerName: "Clients Assigned",
      width: 160,
      renderCell: (params) => {
        const count = Array.isArray(params.value) ? params.value.length : 0;
        return count > 0 ? `${count} Client(s)` : "Not Assigned";
      },
    },
    { field: "department", headerName: "Department", width: 150 },
    // {
    //   field: "defaultPassword",
    //   headerName: "Default Password",
    //   width: 170,
    //   renderCell: (params) => (
    //     <DefaultPasswordCell
    //       name={params.row.name}
    //       phoneNumber={params.row.phoneNumber}
    //     />
    //   ),
    // },
    {
      field: "tasksAssigned",
      headerName: "Tasks Assigned",
      width: 160,
      renderCell: (params) =>
        params.value > 0 ? `${params.value} Task(s)` : "No Tasks",
    },
    {
      field: "accountStatus",
      headerName: "Account Status",
      width: 150,
      renderCell: (params) => {
        const isActive = params.value !== false;
        return (
          <Tooltip title={isActive ? "Active (Click to Deactivate)" : "Deactivated (Click to Activate)"}>
            <Switch
              checked={isActive}
              onChange={() => handleToggleStatus(params.row._id, !isActive)}
              color="success"
            />
          </Tooltip>
        );
      },
    },
    {
      field: "manage",
      headerName: "Manage",
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={(event) => handleOpenMenu(event, params.row._id)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor) && selectedRowId === params.row._id}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={() => handleViewProfile(params.row._id)}>
              View Profile
            </MenuItem>
            <MenuItem onClick={() => handleViewTasks(params.row._id)}>
              View Tasks
            </MenuItem>
            <MenuItem
              onClick={() => {
                const targetMember = rows.find((r) => r._id === params.row._id);
                const isTargetActive = targetMember?.accountStatus !== false;
                handleToggleStatus(params.row._id, !isTargetActive);
                handleCloseMenu();
              }}
              className={
                rows.find((r) => r._id === params.row._id)?.accountStatus !== false
                  ? "text-red-600"
                  : "text-emerald-600"
              }
            >
              {rows.find((r) => r._id === params.row._id)?.accountStatus !== false
                ? "Deactivate Account"
                : "Activate Account"}
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <>
      <SearchBar
        titleOne="Add Team Member"
        onSearch={handleSearch}
        addClientPath="/team-management/add-employee"
        showAddButton={true}
      />
      {/* Outer card with border & shadow for consistency */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="h-[500px]">
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[5, 8, 10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 8, page: 0 } },
            }}
            pagination
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                fontWeight: "bold",
                fontSize: "0.875rem",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.875rem",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f3f4f6",
              },
              border: "none", // removes DataGrid internal border
            }}
          />
        </div>
      </div>
    </>
  );
};

export default TeamManagementHome;
